import {
    DefaultTemplateConfig,
    RecursiveObject,
    RouteConfig,
    Variables,
} from "./ts/types";

export const breakpoints = ["sm", "md", "lg", "xl", "xxl"];

// ****** EXAMPLE INPUT ******
// variables = {
//     "titleSize": {
//         "sm": "14px",
//         "md": "16px",
//     },
//     "descriptionSize": "14px",
//     "titleColor": {
//          "sm": "red",
//          "lg": "blue",
//     },
// }

// ****** OUTPUT ******
// breakpointVariables = {
//     "--title-size-sm": "14px",
//     "--title-size-md" : "16px",
//     "--title-size-lg" : "16px",
//     "--title-size-xl" : "16px",
//     "--title-size-xxl" : "16px",
//     "--description-size-sm": "14px",
//     "--description-size-md": "14px",
//     "--description-size-lg": "14px",
//     "--description-size-xl": "14px",
//     "--description-size-xxl": "14px",
//     "--title-color-sm": "red",
//     "--title-color-md": "red",
//     "--title-color-lg": "blue",
//     "--title-color-xl": "blue",
//     "--title-color-xxl": "blue",
// }

/**
 * Convert variables object to css variables with breakpoints handled
 *
 * @param variables - variables object to convert
 * @param namespace - optional namespace to prefix variable names (e.g., "tabs", "accordion")
 */
export function convertToBreakpointVariables(
    variables: Variables,
    namespace?: string,
): {
    [key: string]: string;
} {
    const breakpointVariables: { [key: string]: string } = {};

    for (const [key, value] of Object.entries(variables)) {
        if (!value) continue;

        // Create variable name with optional namespace prefix
        const varName = namespace ? `${namespace}-${key}` : key;

        if (typeof value === "object") {
            const objValue = value as { [key: string]: string | undefined };
            breakpoints.forEach((breakpoint, index) => {
                breakpointVariables[`--${varName}-${breakpoint}`] =
                    objValue[breakpoint] ||
                    objValue[breakpoints[index - 1]] ||
                    objValue[breakpoints[index - 2]] ||
                    objValue[breakpoints[index - 3]] ||
                    objValue[breakpoints[index - 4]] ||
                    objValue["sm"] ||
                    "unset";
            });
        } else {
            breakpoints.forEach((breakpoint) => {
                breakpointVariables[`--${varName}-${breakpoint}`] = value;
            });
        }
    }

    return breakpointVariables;
}

export function generateGlobalVariables(
    config: Partial<DefaultTemplateConfig>,
) {
    const cssVariables: Record<string, string | undefined> = {};

    function recursiveGenerateCSSVariables(obj: RecursiveObject, prefix = "") {
        for (const key in obj) {
            if (key in obj) {
                const value = obj[key];

                if (typeof value !== "string" && value !== null) {
                    recursiveGenerateCSSVariables(value, `${prefix}-${key}`);
                } else {
                    const variableName = `--${prefix}-${key
                        .replace(/([a-z])([A-Z])/g, "$1-$2")
                        .toLowerCase()}`;
                    cssVariables[variableName] = value || "unset";
                }
            }
        }
    }

    recursiveGenerateCSSVariables(config, "global");

    return cssVariables;
}

export const minifyCSS = (css: string) =>
    css.replace(/\n/g, "").replace(/\s\s+/g, " ");

export function modifyCSSForElement(
    cssString: string | undefined,
    selector: string,
) {
    if (!cssString) return "";

    // At-rules that should have selector prepended to their inner rules
    const scopedAtRules = ["@media", "@supports", "@container", "@layer"];
    // At-rules that should be kept as-is (no selector modification)
    const globalAtRules = [
        "@keyframes",
        "@-webkit-keyframes",
        "@-moz-keyframes",
        "@font-face",
        "@import",
        "@charset",
        "@namespace",
        "@page",
    ];

    // Helper to prepend selector to simple CSS rules
    const prependSelectorToRules = (css: string): string => {
        const rules = css.split("}");
        return rules
            .map((rule) => {
                const trimmed = rule.trim();
                if (trimmed === "") return "";
                const braceIndex = trimmed.indexOf("{");
                if (braceIndex === -1) return `${selector} ${trimmed}`;
                const selectorPart = trimmed.substring(0, braceIndex);
                const bodyPart = trimmed.substring(braceIndex);
                const scopedSelectors = selectorPart
                    .split(",")
                    .map((s) => `${selector} ${s.trim()}`)
                    .join(", ");
                return `${scopedSelectors} ${bodyPart}`;
            })
            .join("\n}\n");
    };

    // Find the closing brace that matches an opening brace (handles nesting)
    const findMatchingBrace = (str: string, startPos: number): number => {
        let braceCount = 1;
        let pos = startPos;
        while (braceCount > 0 && pos < str.length) {
            if (str[pos] === "{") braceCount++;
            if (str[pos] === "}") braceCount--;
            pos++;
        }
        return pos;
    };

    // Find the next at-rule in the string
    const findNextAtRule = (
        str: string,
    ): { index: number; type: string; isScoped: boolean } | null => {
        const allAtRules = [...scopedAtRules, ...globalAtRules];
        let earliest: {
            index: number;
            type: string;
            isScoped: boolean;
        } | null = null;

        for (const atRule of allAtRules) {
            const index = str.indexOf(atRule);
            if (index !== -1 && (earliest === null || index < earliest.index)) {
                earliest = {
                    index,
                    type: atRule,
                    isScoped: scopedAtRules.includes(atRule),
                };
            }
        }
        return earliest;
    };

    // Recursively process CSS (handles nested at-rules)
    const processCSS = (css: string): string => {
        let result = "";
        let remaining = css;

        while (remaining.length > 0) {
            const nextAtRule = findNextAtRule(remaining);

            if (!nextAtRule) {
                // No more at-rules, process remaining as regular rules
                result += prependSelectorToRules(remaining);
                break;
            }

            // Process any rules before this at-rule
            if (nextAtRule.index > 0) {
                const beforeAtRule = remaining.substring(0, nextAtRule.index);
                result += prependSelectorToRules(beforeAtRule);
            }

            // Find the opening brace for this at-rule
            const openBrace = remaining.indexOf("{", nextAtRule.index);
            if (openBrace === -1) {
                // No brace found (e.g., @import or @charset statement)
                const semicolon = remaining.indexOf(";", nextAtRule.index);
                if (semicolon !== -1) {
                    result += remaining.substring(
                        nextAtRule.index,
                        semicolon + 1,
                    );
                    remaining = remaining.substring(semicolon + 1);
                    continue;
                }
                break;
            }

            const atRuleHeader = remaining
                .substring(nextAtRule.index, openBrace)
                .trim();
            const closeBrace = findMatchingBrace(remaining, openBrace + 1);
            const atRuleContent = remaining.substring(
                openBrace + 1,
                closeBrace - 1,
            );

            if (nextAtRule.isScoped) {
                // Scoped at-rules: process inner content recursively
                const processedContent = processCSS(atRuleContent);
                result += `\n${atRuleHeader} {\n${processedContent}\n}\n`;
            } else {
                // Global at-rules: keep content as-is
                result += `\n${atRuleHeader} {\n${atRuleContent}\n}\n`;
            }

            remaining = remaining.substring(closeBrace);
        }

        return result;
    };

    return minifyCSS(processCSS(cssString));
}

export const extractUrl = (input: string) => {
    const match = input.match(/^url\((.+)\)$/);
    return match ? match[1] : "";
};

export const getFormTemplate = (path: string) => {
    if (path.includes("/free-consultation/v1")) {
        return "v1";
    }
    if (path.includes("/free-consultation/v2")) {
        return "v2";
    }
    return null;
};

export const getFinalFontFamily = (
    routeConfig: RouteConfig,
    locale: string,
    fontFamily?: string,
) => {
    const templateGlobalConfig = routeConfig?.templateConfig;
    const fontFamilyFromOtherLanguages =
        templateGlobalConfig?.variantConfig?.otherLanguagesConfig?.[locale]
            ?.fontFamily;

    const fontFamilyFromMainLanguage =
        templateGlobalConfig?.theme?.fontFamily?.replace(" ", "");

    if (templateGlobalConfig) {
        return (
            fontFamilyFromOtherLanguages ||
            fontFamilyFromMainLanguage ||
            (locale === "ar" ? "Almarai" : "Poppins")
        );
    } else {
        return fontFamily || (locale === "ar" ? "Almarai" : "Poppins");
    }
};
